import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { uploadFeedbackEvidence } from "@/api/feedback-api"
import { Image as ImageIcon, Loader2, Upload, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const MAX_BYTES = 5 * 1024 * 1024
const ALLOWED_TYPES = new Set(["image/png", "image/jpeg"])

export type EvidenceState = {
  blobPath: string
  fileName: string
  previewUrl: string
}

type EvidenceUploadProps = {
  value: EvidenceState | null
  onChange: (next: EvidenceState | null) => void
}

/**
 * Drop-zone for an optional evidence screenshot. Uploads PNG/JPEG ≤5MB to
 * /api/feedback/evidence, then surfaces the blob path so the parent step can
 * include it in the feedback payload.
 */
export function EvidenceUpload({ value, onChange }: EvidenceUploadProps) {
  const { t } = useTranslation()
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Revoke any object URL we created when the component unmounts or the
  // selection changes — otherwise the browser leaks the buffer.
  useEffect(() => {
    return () => {
      if (value?.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(value.previewUrl)
      }
    }
  }, [value?.previewUrl])

  const handleFile = useCallback(
    async (file: File) => {
      if (!ALLOWED_TYPES.has(file.type)) {
        toast.error(t("feedback.evidence.errorType", "Only PNG or JPEG images are accepted."))
        return
      }
      if (file.size > MAX_BYTES) {
        toast.error(t("feedback.evidence.errorSize", "Image must be 5 MB or smaller."))
        return
      }
      setUploading(true)
      try {
        const result = await uploadFeedbackEvidence(file)
        const previewUrl = URL.createObjectURL(file)
        onChange({
          blobPath: result.blob_path,
          fileName: file.name,
          previewUrl,
        })
      } catch {
        toast.error(t("feedback.evidence.errorUpload", "Could not upload image. Try again?"))
      } finally {
        setUploading(false)
      }
    },
    [onChange, t]
  )

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setDragOver(false)
      if (e.dataTransfer.files.length > 0) {
        void handleFile(e.dataTransfer.files[0])
      }
    },
    [handleFile]
  )

  const onPick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) void handleFile(file)
      // Reset input so the same file can be picked again after removal.
      e.target.value = ""
    },
    [handleFile]
  )

  // Paste support — the drop zone is focusable and scoped (tabIndex=0) so
  // paste events only land here when the user has clicked/tabbed into it.
  // No global listener, no surprise hijacks.
  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      const items = e.clipboardData.items
      for (const item of items) {
        if (item.kind !== "file" || !item.type.startsWith("image/")) continue
        const file = item.getAsFile()
        if (!file) continue
        e.preventDefault()
        // Clipboard images are usually nameless ("image.png"). Give them a
        // stamped name so support can tell pasted captures apart.
        const named =
          file.name && file.name !== "image.png"
            ? file
            : new File([file], `paste-${Date.now()}.${file.type.endsWith("png") ? "png" : "jpg"}`, {
                type: file.type,
              })
        void handleFile(named)
        return
      }
    },
    [handleFile]
  )

  const handleRemove = useCallback(() => {
    if (value?.previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(value.previewUrl)
    }
    onChange(null)
  }, [onChange, value])

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{t("feedback.evidence.label", "Evidence (optional)")}</p>
      {value ? (
        <div className="border-border bg-muted/20 flex items-start gap-3 rounded-md border p-3">
          <img
            alt={value.fileName}
            className="border-border max-h-20 rounded object-cover"
            src={value.previewUrl}
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{value.fileName}</p>
            <p className="text-muted-foreground text-xs">
              {t("feedback.evidence.uploaded", "Uploaded — will be attached to your report")}
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={handleRemove}>
            <X className="size-4" />
            <span className="sr-only">{t("common.remove", "Remove")}</span>
          </Button>
        </div>
      ) : (
        <div
          role="presentation"
          // Focusable (not a tab stop) so a click puts focus here and paste lands on onPaste;
          // keyboard users use the "Pick image" button.
          tabIndex={-1}
          className={cn(
            "border-border bg-muted/10 hover:bg-accent/30 focus-visible:border-primary focus-visible:ring-primary/30 flex flex-col items-center justify-center gap-2 rounded-md border border-dashed p-6 text-center transition-colors focus-visible:ring-2 focus-visible:outline-none",
            dragOver && "border-primary bg-primary/5"
          )}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onPaste={onPaste}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
        >
          {uploading ? (
            <Loader2 className="text-muted-foreground size-5 animate-spin" />
          ) : (
            <ImageIcon className="text-muted-foreground size-5" />
          )}
          <p className="text-muted-foreground text-xs">
            {uploading
              ? t("feedback.evidence.uploading", "Uploading...")
              : t(
                  "feedback.evidence.drop",
                  "Drop a screenshot, paste one (click here first), or pick a file. PNG/JPEG, ≤5 MB."
                )}
          </p>
          <Button
            disabled={uploading}
            size="sm"
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="size-4" />
            {t("feedback.evidence.pick", "Pick image")}
          </Button>
          <input
            ref={inputRef}
            accept="image/png,image/jpeg"
            className="hidden"
            type="file"
            onChange={onPick}
          />
        </div>
      )}
    </div>
  )
}
